/**
 * ToolBar.tsx
 * 
 * 职责：
 * 1. 提供应用程序的主要工具栏界面
 * 2. 管理绘图模式的切换(绘图、文本、选择、形状)
 * 3. 提供用户友好的工具选择界面
 * 4. 显示当前激活的工具状态
 * 5. 处理工具按钮的点击事件和交互样式
 * 6. 支持通过点击工具右上角的锁图标来锁定工具
 * 7. 支持通过鼠标滚轮切换工具
 */

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { CanvasMode } from '../../types';

interface ToolBarProps {
  canvasMode: CanvasMode;
  onToolChange: (mode: CanvasMode) => void;
  onToolLock: (mode: CanvasMode) => void;
  lockedTool: CanvasMode | null;
}

// 使用forwardRef包装ToolBar组件
const ToolBar = forwardRef<HTMLDivElement, ToolBarProps>(({ 
  canvasMode, 
  onToolChange,
  onToolLock,
  lockedTool
}, ref) => {
  // 添加工具按钮引用数组
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  // 添加弹性效果状态
  const [elasticEffect, setElasticEffect] = useState<{
    active: boolean;
    direction: 'top' | 'bottom' | null;
    scaleY: number;
    scaleX: number;
    origin: string;
    intensity: number; // 添加强度参数
    button: number | null;
  }>({ active: false, direction: null, scaleY: 1, scaleX: 1, origin: 'center center', intensity: 0, button: null });
  
  // 添加高亮位置状态跟踪
  const [prevHighlightPosition, setPrevHighlightPosition] = useState<number | null>(null);
  
  // 添加滚动状态跟踪
  const [wheelDeltaAccumulator, setWheelDeltaAccumulator] = useState<number>(0);
  const MAX_WHEEL_ACCUMULATION = 100; // 最大累积滚动值
  
  // 工具栏交互状态 - 用于控制是否忽略hover效果
  const [isToolbarInteracting, setIsToolbarInteracting] = useState<boolean>(false);
  
  // 添加工具切换冷却时间状态
  const [toolChangeCooldown, setToolChangeCooldown] = useState<boolean>(false);
  const COOLDOWN_DURATION = 200; // 冷却时间（毫秒）
  
  // 添加计时器引用，用于跟踪和重置计时器
  const interactingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 函数：重置所有计时器
  const resetAllTimers = () => {
    // 清除交互状态计时器
    if (interactingTimerRef.current) {
      clearTimeout(interactingTimerRef.current);
      interactingTimerRef.current = null;
    }
    
    // 清除冷却期计时器
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };
  
  // 函数：启动冷却期
  const startCooldown = () => {
    // 先重置所有计时器
    resetAllTimers();
    
    // 设置为交互状态
    setIsToolbarInteracting(true);
    
    // 设置冷却期
    setToolChangeCooldown(true);
    
    // 短期交互状态计时器
    interactingTimerRef.current = setTimeout(() => {
      setIsToolbarInteracting(false);
      interactingTimerRef.current = null;
    }, 100);
    
    // 冷却期计时器
    cooldownTimerRef.current = setTimeout(() => {
      setToolChangeCooldown(false);
      cooldownTimerRef.current = null;
    }, COOLDOWN_DURATION);
  };
  
  // 组件卸载时清理计时器
  useEffect(() => {
    return () => {
      resetAllTimers();
    };
  }, []);
  
  // 所有可用工具的顺序数组
  const availableTools = [
    CanvasMode.SELECT,
    CanvasMode.DRAW,
    CanvasMode.TEXT,
    CanvasMode.SHAPE,
    CanvasMode.CARD,
    CanvasMode.IMAGE
  ];
  
  // 添加和移除滚轮事件监听器
  useEffect(() => {
    // 使用挂载后的DOM元素
    const toolbarElement = typeof ref === 'function' ? null : ref?.current;
    
    if (toolbarElement) {
      // 处理鼠标滚轮事件（定义在useEffect内部避免闭包问题）
      const handleWheel = (event: WheelEvent) => {
        // 如果有工具被锁定，则不切换工具
        if (lockedTool) {
          return;
        }
        
        // 防止页面滚动
        event.preventDefault();
        
        // 设置工具栏交互状态并启动冷却期
        startCooldown();
        
        // 获取当前工具的索引
        const currentIndex = availableTools.indexOf(canvasMode);
        
        // 根据滚动方向决定前进还是后退
        const delta = Math.sign(event.deltaY);
        // 获取滚动力度
        const wheelForce = Math.min(Math.abs(event.deltaY), 50);
        
        // 计算新索引（不再循环选择）
        const newIndex = currentIndex + delta;
        
        // 确保索引在有效范围内
        if (newIndex >= 0 && newIndex < availableTools.length) {
          // 切换到新工具
          onToolChange(availableTools[newIndex]);
          // 重置滚动累积值
          setWheelDeltaAccumulator(0);
        } else {
          // 如果已到达顶部或底部，更新累积滚动值
          const newAccumulator = Math.min(
            wheelDeltaAccumulator + wheelForce,
            MAX_WHEEL_ACCUMULATION
          );
          setWheelDeltaAccumulator(newAccumulator);
          
          // 计算形变强度，基于累积滚动值
          const normalizedIntensity = Math.min(newAccumulator / MAX_WHEEL_ACCUMULATION, 1.0);
          
          // 如果已到达顶部或底部，触发弹性效果
          const direction = delta > 0 ? 'bottom' : 'top';
          const origin = direction === 'top' ? 'center bottom' : 'center top';
          
          // 计算形变参数 - 根据强度在最小和最大值之间插值
          const minScaleY = 1.005; // 最小Y轴拉伸0.5%
          const maxScaleY = 1.02; // 最大Y轴拉伸2%
          const scaleY = minScaleY + normalizedIntensity * (maxScaleY - minScaleY);
          
          const maxScaleX = 0.995; // 最小X轴压缩0.5% 
          const minScaleX = 0.98; // 最大X轴压缩2%
          const scaleX = maxScaleX - normalizedIntensity * (maxScaleX - minScaleX);
          
          // 应用形变效果
          setElasticEffect({
            active: true,
            direction,
            scaleY,
            scaleX,
            origin,
            intensity: normalizedIntensity,
            button: currentIndex
          });
          
          // 形变效果已经在startCooldown中处理了冷却期，这里只需要设置形变动画结束的计时器
          setTimeout(() => {
            setElasticEffect({ 
              active: false, 
              direction: null, 
              scaleY: 1, 
              scaleX: 1, 
              origin,
              intensity: 0,
              button: null
            });
            // 在形变动画结束时重置滚动累积值
            setWheelDeltaAccumulator(0);
          }, 200);
        }
      };
      
      // 添加事件监听器
      toolbarElement.addEventListener('wheel', handleWheel, { passive: false });
      
      // 清理函数
      return () => {
        toolbarElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [canvasMode, lockedTool, onToolChange, availableTools, wheelDeltaAccumulator]);
  
  // 计算高亮背景的位置
  const getHighlightPosition = () => {
    const index = availableTools.indexOf(canvasMode);
    // 每个按钮高度为40px + 上下margin各5px = 50px
    return index * 50;
  };
  
  // 处理点击工具按钮切换工具
  const handleToolButtonClick = (mode: CanvasMode) => {
    const currentIndex = availableTools.indexOf(canvasMode);
    const newIndex = availableTools.indexOf(mode);
    
    // 如果点击的是当前激活的工具或者工具已锁定，不触发效果
    if (currentIndex === newIndex || lockedTool) {
      // 直接调用 onToolChange 不触发形变效果
      onToolChange(mode);
      return;
    }
    
    // 设置工具栏交互状态并启动冷却期
    startCooldown();
    
    // 计算当前高亮位置
    const oldPosition = currentIndex * 50;
    const newPosition = newIndex * 50;
    
    // 计算移动距离
    const distance = Math.abs(newPosition - oldPosition);
    const toolDistance = Math.abs(newIndex - currentIndex);
    
    // 保存当前高亮位置用于下次比较
    setPrevHighlightPosition(oldPosition);
    
    // 只有移动至少两个工具距离才产生形变
    if (toolDistance < 2) {
      onToolChange(mode);
      return;
    }
    
    // 计算形变强度 (0-1之间的值)
    const maxDistance = (availableTools.length - 1) * 50;  // 最大可能距离
    // 映射距离到形变强度，距离越大强度越大
    const normalizedIntensity = Math.min((distance / maxDistance), 1.0);
    
    // 确定是否是到达顶部或底部
    const isAtEdge = newIndex === 0 || newIndex === availableTools.length - 1;
    
    if (isAtEdge) {
      // 确定方向 (前往顶部或底部)
      const direction = newIndex === 0 ? 'top' : 'bottom';
      const origin = direction === 'top' ? 'center bottom' : 'center top';
      
      // 计算形变参数 - 根据强度在最小和最大值之间插值
      const minScaleY = 1.005; // 最小Y轴拉伸0.5%
      const maxScaleY = 1.02; // 最大Y轴拉伸2%
      const scaleY = minScaleY + normalizedIntensity * (maxScaleY - minScaleY);
      
      const maxScaleX = 0.995; // 最小X轴压缩0.5% 
      const minScaleX = 0.98; // 最大X轴压缩2%
      const scaleX = maxScaleX - normalizedIntensity * (maxScaleX - minScaleX);
      
      // 应用形变效果
      setElasticEffect({
        active: true,
        direction,
        scaleY,
        scaleX,
        origin,
        intensity: normalizedIntensity,
        button: currentIndex
      });
      
      // 形变效果已经在startCooldown中处理了冷却期，这里只需要设置形变动画结束的计时器
      setTimeout(() => {
        setElasticEffect({ 
          active: false, 
          direction: null, 
          scaleY: 1, 
          scaleX: 1, 
          origin,
          intensity: 0,
          button: null
        });
      }, 200);
    } else {
      // 对于没有形变效果的情况，startCooldown已经处理了所有状态
    }
    
    // 调用工具切换函数
    onToolChange(mode);
  };
  
  // 工具栏按钮组件
  const ToolButton = ({
    mode,
    icon,
    title,
    isActive = false,
    onClick,
    style,
  }: {
    mode?: CanvasMode;
    icon: string;
    title: string;
    isActive?: boolean;
    onClick?: () => void;
    style?: React.CSSProperties;
  }) => {
    // 添加hover状态
    const [isHovered, setIsHovered] = useState(false);
    
    // 判断工具是否锁定
    const isLocked = mode !== undefined && lockedTool === mode;
    const displayTitle = isLocked 
      ? `${title} (已锁定) - 右键点击解锁` 
      : `${title} - 右键点击锁定`;
    
    // 处理主工具按钮点击
    const handleToolClick = () => {
      if (mode !== undefined) {
        // 触发工具切换(使用我们的自定义函数，带有形变效果)
        handleToolButtonClick(mode);
      }
      
      if (onClick) {
        onClick();
      }
    };

    // 处理右键点击 - 锁定/解锁工具
    const handleRightClick = (e: React.MouseEvent) => {
      // 阻止默认右键菜单
      e.preventDefault();
      
      if (mode !== undefined) {
        // 直接调用锁定/解锁功能
        onToolLock(mode);
      }
    };

    // 处理鼠标进入
    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    // 处理鼠标离开
    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    return (
      <button
        key={mode}
        onClick={() => handleToolClick()}
        onContextMenu={handleRightClick}
        className={`tool-button ${canvasMode === mode ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
        tabIndex={-1}
        style={{
          width: '40px',
          height: '40px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          transition: 'all 0.1s ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          transform: isHovered && !isActive && !isToolbarInteracting && !toolChangeCooldown ? 'scale(1.5)' : 'scale(1)',
          color: isHovered ? '#4a90e2' : (isActive ? '#000' : '#333'),
          zIndex: 2,
          ...style
        }}
        title={displayTitle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {icon}
        {isLocked && (
          <div 
            style={{
              position: 'absolute',
              top: '-5px',             // 按钮有5px的外边距，所以从-5px开始
              left: '-5px',
              width: '50px',
              height: '50px',
              borderRadius: '4px',
              border: '2px solid #4a90e2',
              pointerEvents: 'none',   // 确保该div不会干扰鼠标事件
              boxSizing: 'border-box', // 确保边框不会增加元素尺寸
              zIndex: 1
            }}
          />
        )}
      </button>
    );
  };

  return (
    <div 
      className="toolbar" 
      ref={ref}
      style={{
        position: 'absolute',
        top: '5px',
        left: '5px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        zIndex: 1000,
        overflow: 'hidden', // 确保高亮背景不超出工具栏边界
        transform: elasticEffect.active 
          ? `scaleY(${elasticEffect.scaleY}) scaleX(${elasticEffect.scaleX})` 
          : 'scale(1)',
        transition: 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)',
        transformOrigin: elasticEffect.origin
      }}
      title={lockedTool ? "右键点击工具按钮可以解锁工具" : "右键点击工具按钮可以锁定该工具"}
    >
      {/* 移动的高亮背景 */}
      <div 
        style={{
          position: 'absolute',
          top: `${getHighlightPosition() + 5}px`, // 5px 是顶部padding
          left: '5px', // 5px 是左侧padding
          width: '50px',
          height: '50px',
          backgroundColor: 'rgb(202, 202, 202)',
          borderRadius: '4px',
          transition: 'top 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)', // 添加弹性效果
          zIndex: 1
        }}
      />
      
      {availableTools.map(tool => (
        <ToolButton
          key={tool}
          mode={tool}
          icon={tool === CanvasMode.SELECT ? '☝' : tool === CanvasMode.DRAW ? '✏️' : tool === CanvasMode.TEXT ? 'T' : tool === CanvasMode.SHAPE ? '⬚' : tool === CanvasMode.CARD ? '🗂️' : '📷'}
          title={tool === CanvasMode.SELECT ? '选择工具' : tool === CanvasMode.DRAW ? '绘图工具' : tool === CanvasMode.TEXT ? '文本工具' : tool === CanvasMode.SHAPE ? '形状工具' : tool === CanvasMode.CARD ? '卡片工具' : '图片工具'}
          isActive={canvasMode === tool}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        />
      ))}
    </div>
  );
});

export default ToolBar;